import { AutomationStep } from '@/types/automationSteps.ts';
import {
  AutomationJsonSchema,
  AutomationSchemaRef,
  DatasetType,
  SchemaValidationMode,
} from '@/types/automationFields.ts';
import { PaginatedResponse, Project } from '@/types/common.ts';
import { AutomationModelSettings } from '@/types/llmModels.ts';
import { ExecutionStatus } from '@/types/statuses.ts';
import type { PublicationIntegrity } from '@/types/automationPublications.ts';
import type { TestPlanAnalysis } from '@/types/testPlanAnalysis.ts';

export type AutomationNotificationRecipientMode = 'creator' | 'custom' | 'none';

export type AutomationNotificationEventSettings = {
  mode: AutomationNotificationRecipientMode;
  emails: string[];
};

export type AutomationRunErroredRecoveryPolicy =
  'always_send' | 'suppress_after_successful_error_boundary_recovery';

export type AutomationRunErroredNotificationSettings = AutomationNotificationEventSettings & {
  recovery_policy: AutomationRunErroredRecoveryPolicy;
};

export type AutomationNotificationSettings = {
  run_started: AutomationNotificationEventSettings;
  run_completed: AutomationNotificationEventSettings;
  run_errored: AutomationRunErroredNotificationSettings;
  human_decision_needed?: AutomationNotificationEventSettings;
};

export type CreatorPrincipalType = 'user' | 'org_api_key' | 'service_account';

export type Automation = {
  id: string;
  name: string;
  description: string;
  created_by: string;
  created_by_type: CreatorPrincipalType;
  created_by_display_name: string;
  created_by_email: string;
  created_by_label: string;
  created_at: string;
  // Retired automations remain readable for history, but cannot be selected
  // as a new sub-automation target or admit new runs.
  retired_at?: string | null;
  published_version: AutomationVersion | null;
  published_release_id?: string | null;
  head_revision?: number;
  // latest_run?: Run;
  latest_run_id: string | null;
  latest_run_status: ExecutionStatus | null;
  latest_run_at: string | null;
  run_count: number;
  project: Project;
  notification_settings: AutomationNotificationSettings;
};

export type Automations = PaginatedResponse<Automation>;

export type AutomationRetirementPreflight = {
  retired: boolean;
  historical_dependency_count: number;
  active_parent_head_count: number;
  public_portal_enabled: boolean;
  in_flight_run_count: number;
  latest_parent_heads: AutomationRetirementParentHead[];
  published_parent_heads: AutomationRetirementParentHead[];
};

export type AutomationRetirementParentHead = {
  automation_name: string;
  version_number: number;
};

export type AutomationRetirementResponse = {
  retired_at: string | null;
};

export type AutomationDiscardLatestDraftResponse = {
  automation_id: string;
  discarded_version_id: string;
  restored_version_id: string;
  latest_version_id: string;
  published_version_id: string;
};

export type AutomationPortal = {
  id: string;
  automation_id: string;
  enabled: boolean;
  public_token: string;
  public_slug: string | null;
  title: string;
  description: string;
  run_error_message: string;
  logo_blob_path: string | null;
  logo_mime_type: string;
  logo_url: string | null;
  is_ready: boolean;
  readiness_error: string;
  created_by: string | null;
  created_by_type: CreatorPrincipalType | null;
  created_by_display_name: string | null;
  created_by_email: string | null;
  created_by_label: string;
  created_at: string;
  updated_at: string;
};

export type AutomationPortalRunSummary = {
  public_run_token: string;
  run_id: string;
  run_name: string;
  status: ExecutionStatus;
  preprocessing_error: string | null;
  created_at: string;
};

export type AutomationPortalRuns = {
  results: AutomationPortalRunSummary[];
};

export type PinnedOutput = {
  step_id: string;
  output_id: string;
};

export type BusinessOutcomeAssessmentSource = {
  step_id: string;
  output_id: string;
};

export type BusinessOutcomeAssessmentDeclaration = {
  schema_version: 'business_outcome_assessment.v1';
  label: string;
  instructions: string;
  evidence_sources: BusinessOutcomeAssessmentSource[];
  notify_on_unfavorable: boolean;
};

/** Immutable version-local module data returned with a canonical version. */
export type AutomationLocalSubflowPort = {
  port_id: string;
  name: string;
  dataset_type: DatasetType;
  schema_ref: AutomationSchemaRef | null;
  required: boolean;
};

export type AutomationLocalSubflowOutputPort = AutomationLocalSubflowPort & {
  source_step_id: string;
  source_output_id: string | null;
};

export type AutomationLocalSubflow = {
  subflow_id: string;
  name: string;
  description: string;
  input_ports: AutomationLocalSubflowPort[];
  output_ports: AutomationLocalSubflowOutputPort[];
  declared_effect_boundary: 'none' | 'declared';
  cache_behavior: 'isolated_per_call';
  retry_behavior: 'internal_retry_steps_only';
  failure_propagation: 'propagate_to_caller';
  // Module JSON is execution configuration, not a reviewer-workspace input.
  steps: Record<string, unknown>[];
};

export type AutomationDocumentationSummary = {
  automation_version_id: string;
  event_id: string | null;
  revision_id: string | null;
  alignment_status: 'missing' | 'review_needed' | 'reviewed_aligned';
  reason_codes: string[];
};

export type AutomationDocumentationHistoryEvent = {
  event_id: string;
  sequence: number;
  kind: string;
  status: string;
  policy_resource_scope_reviewed: boolean;
  revision_id: string | null;
  created_at: string;
  created_by: string | null;
  created_by_type: string | null;
  created_by_display_name: string | null;
  review_note: string | null;
};

export type PolicyResourceRef = {
  source_type: 'static' | 'linked';
  reference_id: string;
};

export type AvailablePolicyResource = PolicyResourceRef & {
  reference_name: string;
  ready: boolean;
};

export type BlueprintStarter = {
  body: string;
};

export type AutomationDocumentationReviewFreshness = {
  state: 'current_review' | 'stale_review' | 'no_review' | 'unknown' | 'access_limited';
  basis: 'version_documentation';
  reason_codes: string[];
  limitations: string[];
  source_review: {
    automation_version_id: string;
    event_id: string;
    revision_id: string | null;
    recorded_at: string;
    relationship: 'direct' | 'inherited';
  } | null;
  target_version_id: string;
  scope: {
    behavior_scope: 'whole_version';
    policy_resource_scope_reviewed: boolean;
    policy_resource_count: number;
  };
  comparison: { base_version_id: string; target_version_id: string } | null;
  changed_boundaries: {
    kind:
      | 'configuration_change'
      | 'policy_resource_changed'
      | 'policy_resource_unavailable'
      | 'comparison_unavailable';
    review_subject_ref: string | null;
    subject_kind: string | null;
    facets: string[];
  }[];
  changed_boundary_count: number;
  changed_boundaries_truncated: boolean;
};

export type AutomationVersionDocumentation = AutomationDocumentationSummary & {
  automation_id: string;
  version_creation_kind: string;
  requires_blueprint_reconciliation: boolean;
  behavior_fingerprint: string;
  policy_resource_fingerprint: string | null;
  body: string | null;
  body_sha256: string | null;
  source_kind: string | null;
  policy_resource_scope_reviewed: boolean;
  source_provenance: Record<string, unknown>;
  review_freshness?: AutomationDocumentationReviewFreshness;
  policy_resource_refs: PolicyResourceRef[];
  available_policy_resources: AvailablePolicyResource[];
  history: AutomationDocumentationHistoryEvent[];
  starter_blueprint: BlueprintStarter | null;
};

export type AutomationVersionDocumentationEventDetail = {
  automation_version_id: string;
  event_id: string;
  revision_id: string | null;
  sequence: number;
  kind: string;
  recorded_status: string;
  body: string | null;
  body_sha256: string | null;
  source_kind: string | null;
  source_provenance: Record<string, unknown>;
  policy_resource_refs: PolicyResourceRef[];
  policy_resource_scope_reviewed: boolean;
  behavior_fingerprint: string;
  resource_fingerprints: Record<string, unknown>[];
  review_note: string;
  created_by: string;
  created_at: string;
};

export type AutomationVersionSimple = {
  id: string;
  documentation_summary?: AutomationDocumentationSummary | null;
  created_by: string;
  created_by_type: CreatorPrincipalType;
  created_by_display_name: string;
  created_by_email: string;
  created_by_label: string;
  created_at: string;
  name: string;
  version_number?: number | null;
  creation_kind:
    | 'unknown'
    | 'initial'
    | 'forge_authoring'
    | 'editor_edit'
    | 'model_settings'
    | 'test_suite'
    | 'local_test_package'
    | 'sensitive_setting'
    | 'revert';
  change_note: string;
  source_version_id: string;
  published_at: string | null;
  published_version_count: number | null;
  pinned_steps: string[];
  pinned_outputs: PinnedOutput[];
};

export type AutomationVersion = AutomationVersionSimple & {
  steps: AutomationStep[];
  schemas: AutomationJsonSchema[];
  business_outcome_assessment: BusinessOutcomeAssessmentDeclaration | null;
  business_outcome_assessment_sha256: string;
  available_consumer_schemas?: {
    schema_id: string;
    version: number;
    description: string;
    json_schema: Record<string, unknown>;
    validation_mode: SchemaValidationMode;
    dataset_type: DatasetType;
    provenance: 'platform';
  }[];
  // Optional during a rolling deployment against an older backend. Reviewer
  // surfaces treat their absence as unavailable rather than as no contracts.
  local_subflows?: AutomationLocalSubflow[];
  model_settings: AutomationModelSettings;
};

export type AutomationVersionLocalTestAssetEvidence = {
  path: string;
  role: 'test_source' | 'support_module' | 'fixture' | 'expected_output';
  content_sha256: string;
  byte_count: number;
  original_mime_type: string;
  provenance: {
    kind: 'customer_example' | 'source_run' | 'synthetic' | 'provided';
    source_ref: string;
  };
};

export type AutomationVersionLocalTestTargetEvidence = {
  id: string;
  scope: 'root' | 'local_subflow';
  step_id: string | null;
  call_step_id: string | null;
  subflow_id: string | null;
  module_step_id: string | null;
  contract_sha256: string;
};

export type AutomationVersionLocalTestPackageEvidence = {
  digest: string;
  manifest_sha256: string;
  runner: 'pytest';
  pytest_selectors: string[];
  target_contract_status: 'current' | 'stale';
  targets: AutomationVersionLocalTestTargetEvidence[];
  assets: AutomationVersionLocalTestAssetEvidence[];
};

export type AutomationVersionLocalTestArtifactEvidence = {
  role: 'junit_xml' | 'stdout' | 'stderr' | 'structured_report';
  content_sha256: string;
  byte_count: number;
  original_mime_type: string;
};

export type AutomationVersionLocalTestResultEvidence = {
  node_id: string;
  outcome: 'passed' | 'failed' | 'error' | 'skipped';
  duration_ms: number;
};

export type AutomationVersionLocalTestDiagnosticEvidence = {
  code: string;
  message: string;
};

export type AutomationVersionLocalTestExecutionContext = {
  execution_kind: 'trusted_local_code_execution';
  confirmation_scope: 'trusted_local_code';
  host_resource_sandboxing: 'not_sandboxed';
  builder_run: 'not_created';
  builder_test_classification: 'not_an_effectful_test';
  proof_surface: 'local_deterministic';
};

export type AutomationVersionLocalTestReceiptEvidence = {
  request_id: string;
  plan_digest: string;
  started_at: string;
  completed_at: string;
  outcome: 'passed' | 'failed' | 'error';
  exit_code: number;
  runner_name: 'pytest';
  runner_version: string;
  harness_sha256: string;
  // Optional during a rolling deploy while an older backend has not added the projection yet.
  execution_context?: AutomationVersionLocalTestExecutionContext;
  test_results: AutomationVersionLocalTestResultEvidence[];
  boundary_results?: {
    boundary_id: string;
    outcome: 'passed' | 'failed' | 'error' | 'not_run';
    exit_code: number | null;
    duration_ms: number;
    complete: boolean;
    materialization_intact: boolean;
  }[];
  diagnostics: AutomationVersionLocalTestDiagnosticEvidence[];
  artifacts: AutomationVersionLocalTestArtifactEvidence[];
  created_at: string;
};

export type AutomationVersionLocalTestReceiptSummary = {
  request_id: string;
  started_at: string;
  completed_at: string;
  outcome: 'passed' | 'failed' | 'error';
  exit_code: number;
  runner_name: 'pytest';
  runner_version: string;
  test_result_count: number;
  diagnostic_count: number;
  artifact_count: number;
};

export type AutomationVersionLocalTestEvidence = {
  automation_version_id: string;
  proof_surface: 'local_deterministic';
  // Optional during a rolling deploy while an older backend has not added the projection yet.
  execution_context?: AutomationVersionLocalTestExecutionContext;
  package: AutomationVersionLocalTestPackageEvidence | null;
  latest_receipt: AutomationVersionLocalTestReceiptEvidence | null;
  receipt_history: AutomationVersionLocalTestReceiptSummary[];
};

export type RunAutomationVersion = AutomationVersion & {
  local_subflow_execution_steps_by_call_step_id: Record<string, AutomationStep[]>;
};

export type ContractPublishLifecycleFinding = {
  key: string;
  source: 'input_contract' | 'transform_contract';
  code: string;
  message: string;
  step_id: string;
  step_name: string;
  step_path: string;
  field_id?: string | null;
  field_name?: string | null;
  policy_field?: string | null;
  declared_schema_ref?: { schema_id: string; version: number } | null;
  declared_schema_refs?: { schema_id: string; version: number; output_id?: string | null }[] | null;
  referenced_schema_ref?: { schema_id: string; version: number } | null;
  output_id?: string | null;
  consumer_step_id?: string | null;
  consumer_step_name?: string | null;
  consumer_path?: string | null;
  consumer_port?: ConsumerPortRef | null;
  consumer_port_key?: string | null;
  consumer_port_selector_key?: string | null;
  severity: 'warning' | 'error';
};

export type ConsumerPortRef = {
  step_id: string;
  step_path: string;
  port_kind:
    | 'transform_input'
    | 'validate_input'
    | 'repeat_condition_input'
    | 'conditional_condition_input'
    | 'integration_field';
  binding_name?: string | null;
  field_id?: string | null;
  binding_index?: number | null;
  condition_id?: string | null;
  source_step_id?: string | null;
  source_output_id?: string | null;
  call_step_id?: string | null;
  subflow_id?: string | null;
  module_step_id?: string | null;
};

export type ConsumerContractImpact = {
  producer_step_id: string;
  producer_output_id?: string | null;
  consumer_step_id: string;
  consumer_step_name: string;
  consumer_path: string;
  field_id: string;
  field_name: string;
  compatibility?: string | null;
  message: string;
  consumer_port?: ConsumerPortRef | null;
  consumer_port_key?: string | null;
  consumer_port_selector_key?: string | null;
  previous_consumer_port?: ConsumerPortRef | null;
  previous_consumer_port_key?: string | null;
  previous_consumer_port_selector_key?: string | null;
  candidate_consumer_port?: ConsumerPortRef | null;
  candidate_consumer_port_key?: string | null;
  candidate_consumer_port_selector_key?: string | null;
  change_reasons: (
    | 'producer_contract_changed'
    | 'consumer_contract_changed'
    | 'consumer_binding_changed'
    | 'port_added'
    | 'port_removed'
  )[];
  impact_reason?: string | null;
};

export type AutomationVersionContractPublishLifecycle = {
  disposition: 'publish_eligible' | 'publish_eligible_with_warnings' | 'publish_blocked' | 'evaluation_fault';
  version_fingerprint: string;
  fault_code?: string | null;
  findings: ContractPublishLifecycleFinding[];
};

export type AutomationVersionDependencyInventoryItem = {
  role: 'parent' | 'child';
  automation_name: string;
  version_number: number;
  published_at: string | null;
  relationship_kind: 'sub_automation';
  execution_mode: 'blocking' | 'async';
  is_latest_head: boolean;
  is_published_head: boolean;
};

export type AutomationVersionDependencyInventory = {
  contract_version: 1;
  parents: AutomationVersionDependencyInventoryItem[];
  children: AutomationVersionDependencyInventoryItem[];
};

export type AutomationReadinessFinding = {
  severity: 'info' | 'warning' | 'error';
  code: string;
  message: string;
  step_id?: string | null;
  step_path?: string | null;
  contract_lifecycle_finding_key?: string | null;
  consumer_port?: ConsumerPortRef | null;
  consumer_port_key?: string | null;
  consumer_port_selector_key?: string | null;
};

export type AutomationReadinessContractLifecycle = {
  disposition: 'publish_eligible' | 'publish_eligible_with_warnings' | 'publish_blocked' | 'evaluation_fault';
  fault_code?: string | null;
  finding_count: number;
};

export type AutomationReadiness = {
  automation_version_id: string;
  ready: boolean;
  readiness_scope?: 'version_compatibility';
  publication_integrity?: PublicationIntegrity | null;
  findings: AutomationReadinessFinding[];
  contract_lifecycle?: AutomationReadinessContractLifecycle;
  test_assurance?: AutomationVersionTestAssurance | null;
};

export type AutomationVersionTestCaseAssurance = {
  case_id: string;
  evaluation_id?: string | null;
  proof_surface?: 'native_execution' | 'retained_execution_reevaluation';
  name: string;
  required: boolean;
  status: 'needs_repair' | 'needs_run' | 'running' | 'passed' | 'failed' | 'incomplete';
  message: string;
  latest_run_id?: string | null;
  latest_fixture_source_lineage_sha256?: string | null;
  latest_assertion_suite_sha256?: string | null;
  latest_run_status?: ExecutionStatus | null;
  execution_integrity_status?: 'passed' | 'failed' | 'incomplete' | null;
  execution_integrity_disposition?: AutomationTestExecutionIntegrityDisposition | null;
  assertion_status?: AutomationTestAssertionStatus | null;
};

export type AutomationTestAssertionStatus =
  | 'pending'
  | 'evaluating'
  | 'passed'
  | 'failed'
  | 'incomplete'
  | 'not_configured'
  | 'missing'
  | 'stale';

export type AutomationTestExecutionIntegrityDisposition =
  | 'clean_completion'
  | 'safe_route_attested'
  | 'expected_failure_recovered'
  | 'expected_failure_rethrown'
  | 'expected_failure_terminal'
  | 'unexpected_partial_completion'
  | 'route_mismatch'
  | 'failure_terminal_mismatch'
  | 'recovery_incomplete'
  | 'unrelated_execution_failure'
  | 'invalid_execution_evidence';

export type AutomationVersionTestAssurance = {
  status: 'no_suite' | 'no_cases' | 'not_applicable' | 'needs_repair' | 'needs_run' | 'running' | 'passed' | 'failed' | 'incomplete';
  suite_digest?: string | null;
  message?: string | null;
  cases: AutomationVersionTestCaseAssurance[];
};

export type AutomationVersionTestSuiteCase = {
  id: string;
  name: string;
  description?: string;
  fixture_id: string;
  assertion_suite: Record<string, unknown>;
  intent: AutomationSchedulerTestIntent;
  required?: boolean;
};

export type AutomationTypedFixtureSubstitution = {
  consumer_step_id: string;
  input_group_key: string;
  input_index: number;
  schema_id: string | null;
  schema_version: number | null;
  payload_sha256: string;
  contract_version?: 'producer_fixture.v1';
  dataset_type?: 'json' | 'dataframe';
  validation_kind?: 'schema' | 'representation';
  producer_contract_sha256?: string;
};

type AutomationSchedulerTestIntentBase = {
  target_step_ids: string[];
  fixture_substitutions?: AutomationTypedFixtureSubstitution[];
  expected_routes?: { conditional_step_id: string; condition_id: string }[];
  expected_failure_terminal_step_ids?: string[];
  effect_directives?: { step_id: string; disposition: 'forbid' | 'simulate' }[];
};

export type AutomationSchedulerTestIntent = AutomationSchedulerTestIntentBase &
  (
    | {
        schema_version?: 'scheduler_test_intent.v1';
        expected_root_failure?: never;
      }
    | {
        schema_version: 'scheduler_test_intent.v2';
        expected_root_failure?: {
          step_id: string;
          exception_type: string;
          message_sha256: string;
        } | null;
      }
  );

export type FixtureDependencyDiagnostic = {
  code: 'fixture_controlling_condition_inputs_unbound';
  severity: 'warning';
  message: string;
  controlling_step_id: string;
  retained_upstream_step_ids: string[];
  suggested_input_selectors: {
    consumer_step_id: string;
    input_group_key: string;
    input_index: number;
  }[];
};

export type AutomationVersionTestSuite = {
  automation_version_id: string;
  selection_generation?: number;
  latest_version_id?: string | null;
  published_version_id?: string | null;
  head_revision?: number;
  suite_id?: string | null;
  digest?: string | null;
  snapshot?: {
    schema_version: 'automation_version_test_suite.v2';
    cases: AutomationVersionTestSuiteCase[];
  } | null;
  authoring_snapshot: {
    schema_version: 'automation_version_test_suite.v2';
    cases: AutomationVersionTestSuiteCase[];
  } | null;
  assurance?: AutomationVersionTestAssurance | null;
};

export type AutomationTestFixture = {
  parent_fixture_id?: string | null;
  parent_definition_sha256?: string | null;
  derivation_input_manifest?: {
    source_ref: string;
    binding: {
      kind: 'trigger';
      trigger_step_id: string;
      trigger_id: string;
    } | {
      kind: 'input';
      consumer_path: string;
      input_group_key: string;
      input_index: number;
      schema_id?: string;
      schema_version?: number;
    };
    evidence: {
      kind: 'file' | 'text' | 'json';
      origin: {
        origin?: 'synthetic' | 'provided' | 'supplied' | 'source_run';
        fixture_mode?: string;
        source_run_id?: string | null;
        provided_source_ref?: string | null;
      };
    };
  }[];
  id: string;
  automation_id: string;
  source_run_id: string | null;
  name: string;
  scenario_label: string;
  status: 'active' | 'retired';
  mode:
    | 'source_lineage'
    | 'source_lineage_with_input_overrides'
    | 'provided_file_seed'
    | 'synthetic_file_seed'
    | 'synthetic_trigger_seed'
    | 'trigger_input_bundle';
  created_against_version_id?: string | null;
  input_override_manifest: AutomationTestFixtureInputManifestEntry[];
  trigger_override_manifest: AutomationTestFixtureTriggerManifestEntry[];
  file_seed_manifest?: AutomationTestFixtureFileSeedManifestEntry | null;
  text_seed_manifest?: AutomationTestFixtureTextSeedManifestEntry[];
  trigger_input_manifest?: {
    trigger_step_id: string;
    trigger_id: string;
    kind: 'file' | 'text' | 'json';
    origin: 'synthetic' | 'provided';
    trigger_input_identity_sha256: string;
    value_sha256: string;
    byte_count: number;
    canonical_file_sha256?: string | null;
    original_mime_type?: string | null;
    file_extension?: string | null;
    provided_source_ref?: string | null;
    schema_id?: string | null;
    schema_version?: number | null;
    authorization_attested: true;
    provider_proof: false;
    dataset_processing_status: 'pending' | 'processing' | 'ready' | 'failed';
  }[];
  fixture_processing_status?: 'pending' | 'processing' | 'ready' | 'failed' | null;
  source_lineage_sha256: string;
  definition_sha256: string;
  input_override_count: number;
  trigger_override_count: number;
};

export type AutomationVersionTestFixtureBinding = {
  automation_version_id: string;
  fixture_id: string;
  compatible: boolean;
  fixture_substitutions: AutomationTypedFixtureSubstitution[];
  issue?: { reason_code: string; message: string } | null;
};

export type AutomationVersionTestSuitePreviewResponse = {
  selection_generation?: number;
  automation_version_id: string;
  projected_assurance_status: 'no_cases' | 'not_applicable' | 'needs_repair' | 'needs_run';
  required_cases_executable: boolean;
  all_cases_executable: boolean;
  case_preflights: {
    case_id: string;
    name: string;
    required: boolean;
    eligible: boolean;
    issues: { reason_code: string; message: string; details: Record<string, unknown> }[];
    warnings?: { reason_code: string; message: string; details: Record<string, unknown> }[];
    execution: TestPlanAnalysis & {
      digest?: string;
      target_step_ids: string[];
      scheduled_step_ids: string[];
      blocked_effect_step_ids: string[];
    };
  }[];
  normalized_snapshot: {
    schema_version: 'automation_version_test_suite.v2';
    cases: AutomationVersionTestSuiteCase[];
  };
};

export type AutomationTestFixtureInputManifestEntry = {
  consumer:
    | { scope: 'root'; step_id: string }
    | { scope: 'local_subflow'; call_step_id: string; subflow_id: string; module_step_id: string };
  runtime_step_id: string;
  input_group_key: string;
  input_index: number;
  payload_kind: 'schema_validated' | 'opaque_json' | 'producer_validated';
  schema_id?: string | null;
  schema_version?: number | null;
  fixture_label?: string | null;
  input_identity_sha256: string;
  payload_sha256: string;
  byte_count: number;
  dataset_type?: 'json' | 'dataframe';
  validation_kind?: 'schema' | 'representation' | null;
};

export type AutomationTestFixtureTriggerManifestEntry = {
  trigger_step_id: string;
  trigger_id: string;
  schema_id: string;
  schema_version: number;
  trigger_identity_sha256: string;
  payload_sha256: string;
  byte_count: number;
};

export type AutomationTestFixtureFileSeedManifestEntry = {
  provided_source_ref?: string | null;
  trigger_step_id: string;
  trigger_id: string;
  trigger_input_identity_sha256: string;
  uploaded_file_sha256: string;
  uploaded_byte_count: number;
  canonical_file_sha256: string;
  file_extension: string;
  original_mime_type: string;
  dataset_processing_status: 'pending' | 'processing' | 'ready' | 'failed';
};

export type AutomationTestFixtureTextSeedManifestEntry = {
  trigger_step_id: string;
  trigger_id: string;
  trigger_input_identity_sha256: string;
  value_sha256: string;
  byte_count: number;
  dataset_processing_status: 'pending' | 'processing' | 'ready' | 'failed';
};

export type AutomationTestFixtureInputCandidate = {
  consumer:
    | { scope: 'root'; step_id: string }
    | { scope: 'local_subflow'; call_step_id: string; subflow_id: string; module_step_id: string };
  runtime_step_id: string;
  step_name: string;
  input_group_key: string;
  input_index: number;
  input_name?: string | null;
  binding_name?: string | null;
  source_step_id: string;
  output_id?: string | null;
  schema_id?: string | null;
  schema_version?: number | null;
  input_identity_sha256: string;
  dataset_type?: 'json' | 'dataframe';
  validation_kind?: 'schema' | 'representation';
  supported_payload_kinds?: ('schema_validated' | 'opaque_json' | 'producer_validated')[];
};

export type AutomationTestFixtureTriggerCandidate = {
  trigger_step_id: string;
  trigger_step_name: string;
  trigger_id: string;
  input_name: string;
  is_required: boolean;
  schema_id: string;
  schema_version: number;
  trigger_identity_sha256: string;
};

export type AutomationTestFixtureInputIneligibility = {
  consumer:
    | { scope: 'root'; step_id: string }
    | { scope: 'local_subflow'; call_step_id: string; subflow_id: string; module_step_id: string };
  runtime_step_id: string;
  step_name: string;
  input_group_key: string;
  input_index: number;
  reason_code: string;
  message: string;
};

export type AutomationTestFixtureTriggerIneligibility = {
  trigger_step_id: string;
  trigger_step_name: string;
  trigger_id: string;
  input_name: string;
  reason_code: string;
  message: string;
};

export type AutomationTestFixtureInputDiagnostic = {
  code: string;
  message: string;
  consumer?: AutomationTestFixtureInputCandidate['consumer'] | null;
  input_group_key?: string | null;
  input_index?: number | null;
  trigger_step_id?: string | null;
  trigger_id?: string | null;
};

export type AutomationTestFixtureInputOverridesPreviewResponse = {
  automation_version_id: string;
  valid: boolean;
  input_overrides: AutomationTestFixtureInputManifestEntry[];
  trigger_overrides: AutomationTestFixtureTriggerManifestEntry[];
  diagnostics: AutomationTestFixtureInputDiagnostic[];
};

export type AutomationVersionTestCaseRun = {
  run_id: string;
  case_id: string;
  case_digest: string;
  suite_digest: string;
  plan_digest: string;
  fixture_id: string;
  fixture_source_lineage_sha256: string;
  fixture_definition_sha256: string;
  assertion_suite_sha256: string;
  run_status: string;
  created_at: string;
};

export type AutomationVersionsSimple = PaginatedResponse<AutomationVersionSimple>;

export type VersionComparisonV3Facet = {
  kind:
    | 'added'
    | 'removed'
    | 'renamed'
    | 'moved'
    | 'behavior_changed'
    | 'input_binding_changed'
    | 'output_contract_changed'
    | 'integration_action_changed'
    | 'integration_setting_changed'
    | 'schema_changed'
    | 'model_setting_changed'
    | 'test_assurance_changed';
  summary: string;
};

export type VersionComparisonV3AffectedItem = {
  label: string;
  relationships: ('data_input' | 'control_wait' | 'validation_block')[];
};

export type VersionComparisonV3Subject = {
  review_subject_ref: string;
  subject_kind: string;
  subject_label: string;
  previous_subject_label: string | null;
  facets: VersionComparisonV3Facet[];
  review_order: number;
  affected_items: VersionComparisonV3AffectedItem[];
};

export type VersionComparisonV3 = {
  contract_version: 3;
  base_version: Pick<AutomationVersionSimple, 'name' | 'version_number' | 'created_at' | 'published_at'>;
  target_version: Pick<AutomationVersionSimple, 'name' | 'version_number' | 'created_at' | 'published_at'>;
  subjects: VersionComparisonV3Subject[];
  target_fingerprint: string;
};

export type AutomationVersionReviewSummaryHighlight = {
  review_subject_refs: string[];
  explanation: string;
};

export type AutomationVersionReviewSummary = {
  base_version_id: string;
  target_version_id: string;
  target_fingerprint: string;
  overview: string;
  highlights: AutomationVersionReviewSummaryHighlight[];
  omitted_subject_count: number;
};

export type VersionComparisonV3DetailBlock = {
  kind:
    | 'binding_change'
    | 'scalar_change'
    | 'selection_change'
    | 'schema_change'
    | 'collection_change'
    | 'text_diff'
    | 'code_diff'
    | 'secret_changed'
    | 'unavailable';
  label: string;
  before: string | null;
  after: string | null;
  summary: string | null;
};

export type VersionComparisonV3Detail = {
  contract_version: 3;
  review_subject_ref: string;
  detail_blocks: VersionComparisonV3DetailBlock[];
  truncated: boolean;
  unavailable_reason: string | null;
  target_fingerprint: string;
  navigation_step_id: string | null;
};

type AutomationVersionLabelLike = Pick<
  AutomationVersionSimple,
  'name' | 'version_number' | 'published_version_count'
>;

export const getAutomationVersionLabel = (
  version?: AutomationVersionLabelLike | null,
): string | null => {
  if (typeof version?.version_number === 'number') {
    return `v${version.version_number}`;
  }
  if (typeof version?.published_version_count === 'number') {
    return `v${version.published_version_count}`;
  }
  return null;
};

export const getAutomationVersionDisplayName = (
  version?: AutomationVersionLabelLike | null,
): string => {
  const label = getAutomationVersionLabel(version);
  const customName = version?.name?.trim();

  if (label && customName && customName !== label) {
    return `${label} · ${customName}`;
  }
  if (label) {
    return label;
  }
  if (customName) {
    return customName;
  }
  return 'Untitled version';
};
